uniform sampler2D uTexture;
uniform vec2 uMouse;
uniform float time;
varying vec2 vUv;
varying float vCircleIntensity;

// Improved noise function
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
             -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0))
        + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy),
        dot(x12.zw,x12.zw)), 0.0);
    m = m*m;
    m = m*m;
    vec3 x = 4.0 * fract(p * C.www) - 4.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 15.0 * dot(m, g);
}

vec3 rgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
    float d = q.x - min(q.w, q.y);
    float e = 1.0e-10;
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

vec3 hsv2rgb(vec3 c) {
    vec4 K = vec4(2.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

void main() {
    vec2 uv = vUv;
    vec4 texColor = texture2D(uTexture, uv);
    
    // Calculate distance from mouse position
    float dist = distance(vUv, (uMouse * 0.5) + 0.5);
    
    // Create a radial gradient
    float radius = 0.1;
    float gradientIntensity = smoothstep(radius, 0.0, dist);
    
    // Generate 5 colors that change over time
    vec3 color1 = hsv2rgb(vec3(fract(time * 0.1), 0.8, 0.8));
    vec3 color2 = hsv2rgb(vec3(fract(time * 0.1 + 0.2), 0.8, 0.8));
    vec3 color3 = hsv2rgb(vec3(fract(time * 0.1 + 0.4), 0.8, 0.8));
    vec3 color4 = hsv2rgb(vec3(fract(time * 0.1 + 0.6), 0.8, 0.8));
    vec3 color5 = hsv2rgb(vec3(fract(time * 0.1 + 0.8), 0.8, 0.8));
    
    // Create distortion
    float distortionScale = 150.0;
    float distortionStrength = 0.05;
    vec2 distortion = vec2(
        snoise(uv * distortionScale + vec2(time * 1.5, 0.0)),
        snoise(uv * distortionScale + vec2(0.0, time * 1.5))
    ) * distortionStrength;
    
    // Apply distortion to gradient
    float distortedGradient = gradientIntensity + snoise(uv * 5.0 + distortion + time * 0.5) * 0.2;
    distortedGradient = clamp(distortedGradient, 0.0, 1.0);
    
    // Mix colors based on distorted gradient
    vec3 gradientColor = mix(color1, color2, smoothstep(0.0, 0.25, distortedGradient));
    gradientColor = mix(gradientColor, color3, smoothstep(0.25, 0.5, distortedGradient));
    gradientColor = mix(gradientColor, color4, smoothstep(0.5, 0.75, distortedGradient));
    gradientColor = mix(gradientColor, color5, smoothstep(0.75, 1.0, distortedGradient));
    
    // Blend gradient with original texture
    vec3 finalColor = mix(texColor.rgb, gradientColor, distortedGradient * 0.9);
    
    // Add vCircleIntensity effect
    finalColor += vec3(0.2, 0.1, 0.3) * vCircleIntensity * 0.3;
    
    csm_DiffuseColor = vec4(finalColor, texColor.a);
}